import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Nav, UncontrolledCollapse } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";

import { Translate } from "@/helpers/Translate";
import { actionCreators } from "@/store/student";

const t = new Translate();

/** Returns the first word of a name ("John Smith" -> "John"). */
function getFirstWord(str: string) {
  const arr = str?.trim()?.split(" ");
  if (arr?.length) {
    return arr[0];
  }
  return "";
}

/** A single side-nav entry: Font Awesome icon + translated label. */
interface SideNavItem {
  to: string;
  icon: string;
  label: string;
  iconClassName?: string;
}

// Items shown after the "Students" group, in display order.
const accountItems: SideNavItem[] = [
  { to: "/dashboard/finance", icon: "fas fa-wallet", label: "Smart Wallet" },
  { to: "/dashboard/parentcard", icon: "fas fa-id-card", label: "Parent Card" },
];

const attendanceItems: SideNavItem[] = [
  { to: "/dashboard/latearrival", icon: "far fa-clock", label: "Late Arrival" },
  { to: "/dashboard/earlydismissal", icon: "fas fa-door-open", label: "Early Dismissal" },
  { to: "/dashboard/excuses", icon: "fas fa-bed", label: "Excuses" },
];

const meetingItems: SideNavItem[] = [
  { to: "/dashboard/meetings", icon: "fas fa-comments", label: "Request Meeting" },
  { to: "/dashboard/groupmeetings", icon: "fas fa-users", label: "Group Meetings" },
];

const studentLifeItems: SideNavItem[] = [
  { to: "/dashboard/academicinterventions", icon: "fas fa-book", label: "Academic interventions" },
  { to: "/dashboard/interventions", icon: "fas fa-ban", label: "Character Interventions" },
  { to: "/dashboard/clinicvisits", icon: "fas fa-stethoscope", label: "Clinic Visits" },
  {
    to: "/dashboard/WallOfLove",
    icon: "fas fa-heart",
    label: "Wall of Love",
    iconClassName: "pp-nav-icon--love",
  },
  { to: "/dashboard/cafeteria", icon: "fas fa-apple-alt", label: "Cafeteria" },
];

/** Renders one internal navigation link. */
function SideNavLink({ item }: { item: SideNavItem }) {
  const label = t.translate(item.label);
  return (
    <NavLink className="nav-link" to={item.to} title={label}>
      <i
        className={`pp-nav-icon ${item.icon} ${item.iconClassName ?? ""}`}
        aria-hidden="true"
      ></i>
      <span className="pp-nav-label">{label}</span>
    </NavLink>
  );
}

/**
 * Side navigation of the dashboard.
 *
 * Loads the guardian's students on mount and lists them under a
 * collapsible "Students" group. Styles live in ./DashBoard.css.
 */
export default function SideNav(props: any) {
  const transjwt: any = useSelector((state: any) => state.Parent.transjwt);
  const students = useSelector((state: any) => state.Student.students);
  const guardian = useSelector((state: any) => state.Parent.guardian);
  const dispatch: any = useDispatch();
  const [isOpenStudentsMenu, setIsOpenStudentsMenu] = useState(false);

  useEffect(() => {
    dispatch(actionCreators.GetStudents({ id: guardian.id }));
  }, [dispatch, guardian.id]);

  return (
    <div id="layoutSidenav_nav">
      <nav
        className="sb-sidenav accordion sb-sidenav-dark navbar-header"
        id="sidenavAccordion"
        aria-label="Main navigation"
      >
        <div className="sb-sidenav-menu">
          <Nav className="nav">
            <SideNavLink
              item={{ to: "/dashboard/home", icon: "fas fa-home", label: "Home" }}
            />

            {/* Students: link to the list + collapsible list of each student */}
            <div className="dropdown-container">
              <div className="pp-nav-group">
                <SideNavLink
                  item={{
                    to: "/dashboard/viewstudents",
                    icon: "fas fa-user-graduate",
                    label: "Students",
                  }}
                />
                <button
                  type="button"
                  id="toggler-students"
                  className={`pp-nav-toggle ${isOpenStudentsMenu ? "is-open" : ""}`}
                  aria-label={t.translate("Students")}
                  aria-expanded={isOpenStudentsMenu}
                  aria-controls="students-collapse"
                >
                  <i className="fas fa-chevron-down" aria-hidden="true"></i>
                </button>
              </div>
              <UncontrolledCollapse
                id="students-collapse"
                toggler="#toggler-students"
                onEntering={() => setIsOpenStudentsMenu(true)}
                onExiting={() => setIsOpenStudentsMenu(false)}
              >
                <div className="pp-nav-sublist">
                  {students?.map((student: any, key: any) => (
                    <NavLink
                      key={key}
                      title={student.fullname}
                      className="nav-link"
                      to={`/dashboard/viewstudents/${student.id}`}
                    >
                      <i className="pp-nav-icon fas fa-user" aria-hidden="true"></i>
                      <span className="pp-nav-label">
                        {getFirstWord(student.fullname)}
                      </span>
                    </NavLink>
                  ))}
                </div>
              </UncontrolledCollapse>
            </div>

            {accountItems.map((item) => (
              <SideNavLink key={item.to} item={item} />
            ))}

            <div className="pp-nav-divider" role="separator"></div>
            {attendanceItems.map((item) => (
              <SideNavLink key={item.to} item={item} />
            ))}

            <div className="pp-nav-divider" role="separator"></div>
            {meetingItems.map((item) => (
              <SideNavLink key={item.to} item={item} />
            ))}

            <div className="pp-nav-divider" role="separator"></div>
            {studentLifeItems.map((item) => (
              <SideNavLink key={item.to} item={item} />
            ))}

            {/* Transportation is a separate app, opened in a new tab */}
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link"
              href={`https://transportation.ivyis.org/?hash=` + transjwt}
            >
              <i className="pp-nav-icon fas fa-bus" aria-hidden="true"></i>
              <span className="pp-nav-label">{t.translate("Transportation")}</span>
              <i
                className="pp-nav-external fas fa-external-link-alt"
                aria-hidden="true"
              ></i>
            </a>
          </Nav>
        </div>
      </nav>
    </div>
  );
}
